import {
  Content,
  ContentAccessUser,
  ContentAudience,
  ContentHistory,
  ContentWorkflow,
  PublicationFeedback,
  ReviewRequestStatus,
  UserProfile,
  WorkflowState,
  InlineComment,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React from 'react';
import { ContentWorkingCopyManager } from '../../../common/ContentWorkingCopyManager';
import DraftViewerRole from '../../../components/utils/DraftViewerRole';
import AppContextProps from '../../../contexts/AppContextProps';
import { HelpPanelContextProps } from '../../../contexts/HelpPanelTypes';
import { ViewerType } from '../../../model/Enum';

/**
 * Props for all ContentData subclasses.
 *
 * This is a MEGA-INTERFACE (~70 props) that carries everything needed by both
 * readonly and edit views. Most props are passed straight through from ContentViewer.render()
 * via DraftContentData. Many are optional because not all views need all data.
 */
export interface ContentDataProps extends AppContextProps, HelpPanelContextProps {
  // ─── Core content data ──────────────────────────────────────────────────────
  isEditingAllowed: boolean;
  coAuthors?: ContentAccessUser[];
  peerReviewers?: ContentAccessUser[];
  contentAudience: ContentAudience;
  contentOwner?: UserProfile;
  contentId: string;
  workflowId: number;
  content: Content;
  contentLastModified: DateTime;

  // ─── Display flags (controlled by ContentViewer subclass hooks) ─────────────
  showContentControls: boolean; // Whether to show the edit toolbar
  showTechwriterMetadata: boolean; // Whether to show tech-writer-specific fields
  showReviewFeedback?: boolean; // Whether to show review events
  showSectionalComments?: boolean; // Whether to show per-section comments
  hideEmptySections: boolean;

  // ─── Workflow state ─────────────────────────────────────────────────────────
  workflow?: ContentWorkflow;
  workflowEvents?: any[];
  workflowHistory?: ContentHistory[];

  // ─── Callbacks to ContentViewer (parent) ────────────────────────────────────
  onEnableEditMode?: () => Promise<void>; // Called when user clicks "Edit"
  onDisableEditMode?: () => Promise<void>; // Called when user exits edit mode
  onContentDataChanged: (updatedContent: Content) => Promise<void>;
  onContentAudienceChanged: (updatedAudience: ContentAudience) => Promise<void>;
  onValidationError?: (validationError?: any) => Promise<void>;
  onTabViewChange?: (tabView: string) => void;

  // ─── Publication & review ───────────────────────────────────────────────────
  publicationDate?: DateTime;
  validationError?: any;
  onSubmitForReview?: () => void;
  onSubmitForReviewComplete?: () => void;
  draftViewerRole: DraftViewerRole;
  submittingForReview?: boolean;
  publicationState?: WorkflowState;
  onReviewComplete?: () => void;
  isAdminView?: boolean;
  actionForm?: React.ReactNode; // Injected status/action section from ContentViewer subclass
  viewerType?: ViewerType;
  publicationFeedbacks?: PublicationFeedback[];
  reviewRequestStatus?: ReviewRequestStatus;
  showTabView?: boolean;
  approvalDisabled?: boolean;
  disableApproveMessage?: string;
  showLastBarRaiserEvaluation?: string;

  // ─── Inline comments ────────────────────────────────────────────────────────
  inlineComments?: InlineComment[];
  updateInlineCommentsLocation?: (inlineComments: InlineComment[]) => void;
  updatedInlineComments?: InlineComment[];
  setInlineCommentsData?: (comments: InlineComment[]) => void;
  setShowInlineCommentsInEditMode?: (show: boolean) => void;
  showInlineCommentsInEditMode?: boolean;
  showInlineCommentsInReviewerPage?: boolean;
  setShowInlineCommentsInReviewerPage?: (show: boolean) => void;
  isCommentingModeEnabled?: boolean;
  enableCommentMode?: (enabled: boolean) => void;
  showInlineCommentsInPublisherPage?: boolean;
  setShowInlineCommentsInPublisherPage?: (show: boolean) => void;
  reFetchInlineComments?: () => Promise<void>;
  showCodeValidationError?: boolean;
  onReportToAuthor?: () => void;
  showFeedbackFormInUpdateRequests?: boolean;
}

/**
 * Top-level state for ContentData. Tracks:
 * - whether the user is currently editing (drives which view renders)
 * - loading state so render() returns null until checkEditingEnabled() resolves
 *   (prevents a flash of the wrong view before the lock check completes).
 */
export interface ContentDataState {
  isEditingEnabled: boolean;
  isLoading: boolean;
}

/**
 * TEMPLATE METHOD base class that switches between readonly and edit views.
 *
 * This is the SECOND Template Method in the system (the first is ContentViewer).
 * ContentViewer.render() creates a DraftContentData (which extends this class).
 * This class's render() calls checkEditingEnabled() to decide which view to show,
 * then delegates to renderReadonlyView() or renderEditView().
 *
 * Inheritance tree:
 *   ContentData
 *     └── DraftContentData (overrides checkEditingEnabled to check lock ownership)
 *           - renderReadonlyView → DraftReadonlyContentData
 *           - renderEditView → DraftEditContentData
 *
 * Hook methods for subclasses:
 *   - checkEditingEnabled()           → determines if user is in edit mode
 *   - renderReadonlyView()            → renders the readonly content view
 *   - renderEditView()                → renders the edit content view
 *   - beforeSaving()                  → validation before save
 *   - saveChanges(unlock)             → persist changes to backend
 *   - discardChanges()                → discard edits and unlock
 *   - exitEditModeAndSubmitForReview()→ save + submit for review
 */
class ContentData extends React.Component<ContentDataProps, ContentDataState> {
  private static defaultProps = {
    workflow: new ContentWorkflow(),
    workflowEvents: [],
    workflowHistory: [],
    publicationFeedbacks: [],
    showTabView: true,
  };

  // Manages local working copies (auto-saved drafts in localStorage/IndexedDB)
  protected workingCopyMngr: ContentWorkingCopyManager;

  constructor(props: ContentDataProps) {
    super(props);
    this.workingCopyMngr = new ContentWorkingCopyManager();
    const { isEditingAllowed: isEditingEnabled } = this.props;
    this.state = {
      isEditingEnabled,
      isLoading: true,
    };
  }

  // On mount: check if editing should be enabled (async because DraftContentData
  // checks lock ownership which may need state)
  public async componentDidMount() {
    const isEditingEnabled = await this.checkEditingEnabled();
    this.setState({
      isEditingEnabled,
      isLoading: false,
    });
  }

  // Re-check editing state when workflow or isEditingAllowed props change
  // (e.g. when another user acquires/releases the lock)
  public async componentDidUpdate(prevProps: ContentDataProps, prevState: ContentDataState) {
    const { workflow, isEditingAllowed } = this.props;
    if (isEditingAllowed !== prevProps.isEditingAllowed || workflow !== prevProps.workflow) {
      const isEditingEnabled = await this.checkEditingEnabled();
      this.setState({
        isEditingEnabled,
      });
    }
  }

  // THE TEMPLATE METHOD: show nothing while loading, then delegate to readonly or edit view
  public render() {
    const { isEditingEnabled, isLoading } = this.state;
    if (isLoading) {
      return null;
    }
    return !isEditingEnabled ? this.renderReadonlyView() : this.renderEditView();
  }

  // ─── HOOK METHODS (overridden by subclasses) ───────────────────────────────

  // Default: just check the isEditingAllowed prop.
  // DraftContentData overrides to also check if the current user holds the workflow lock.
  protected checkEditingEnabled = async () => {
    const { isEditingAllowed } = this.props;
    return isEditingAllowed;
  };

  // Renders the readonly view. Overridden by DraftContentData → DraftReadonlyContentData.
  protected renderReadonlyView = () => {
    return null;
  };

  // Renders the edit view. Overridden by DraftContentData → DraftEditContentData.
  protected renderEditView = () => {
    return null;
  };

  // Pre-save validation hook. Returns true if save should proceed.
  protected beforeSaving = async () => {
    return true;
  };

  // Persists content changes to the backend. If unlock=true, also releases the edit lock.
  protected saveChanges = async (unlock: boolean) => {
    return null;
  };

  // Discards all unsaved changes and releases the edit lock.
  protected discardChanges = async () => {
    return null;
  };

  // Discards changes, then submits the content for peer review.
  protected exitEditModeAndSubmitForReview = async () => {
    return null;
  };
}

export default ContentData;
